/**
 * Pure helper used by `seedOfflineICodePrefills` in document_service.ts.
 * Extracted so the seed-payload construction can be unit-tested without Dexie
 * or HLC mocks. The orchestrator in document_service handles the I/O.
 *
 * iCode prefill data is now supplied by the server (the client API's
 * `icode_prefills` map — see `Client['icode_prefills']`), so the offline app no
 * longer scans prior cached documents to reconstruct it. We only build the
 * `field_prefills` entries (`source`, `requires_confirmation`) the server would
 * have emitted, keeping byte-parity with `ICodePrefilledField#prefill_source`.
 */

import type { Document } from '../types/document';
import type { ICodePrefill } from '../types/client';

interface IcodeFieldDef {
  icode?: string;
}

export interface SeedPayload {
  fieldPrefills: NonNullable<Document['field_prefills']>;
  superFormSeeds: Record<string, unknown>;
}

/**
 * Maps the new template's iCode fields onto the server-provided iCode prefill
 * map and produces the two writes the orchestrator commits: `field_prefills`
 * (per-field PARC metadata) and `super_form` value carry-overs. Pending PARC
 * slugs are derived on read as
 * `entries(field_prefills).filter(requires_confirmation) − confirmed`,
 * so no separate `unconfirmed_prefills` array is persisted.
 *
 * The `source` string matches the server's `ICodePrefilledField#prefill_source`
 * verbatim ("interRAI iCode <iCode>") so the local seed and the eventual
 * server-emitted prefills produce byte-identical labels — no UI flicker when
 * the background sync response merges over the local seed. `date` and
 * `document_name` are carried straight through from the server map (omitted
 * when absent), matching the server's additive `field_prefills` keys.
 *
 * Value guard: `raw_value` is seeded as-is (a string from the API; the
 * super-form framework coerces per field `data_type` on render). Only
 * `undefined`/`null`/`''` are skipped — `0` is a legitimate value for many
 * iCodes.
 */
export function buildSeedPayload(
  newFields: Record<string, IcodeFieldDef | null | undefined>,
  icodePrefills: Record<string, ICodePrefill>,
): SeedPayload {
  const fieldPrefills: SeedPayload['fieldPrefills'] = {};
  const superFormSeeds: SeedPayload['superFormSeeds'] = {};

  for (const [fieldName, def] of Object.entries(newFields)) {
    const ic = def?.icode;
    if (!ic) continue;
    const hit = icodePrefills[ic];
    if (!hit || hit.raw_value === undefined || hit.raw_value === null || hit.raw_value === '') continue;
    fieldPrefills[fieldName] = {
      source: `interRAI iCode ${ic}`,
      requires_confirmation: true,
      ...(hit.date ? { date: hit.date } : {}),
      ...(hit.document_name ? { document_name: hit.document_name } : {}),
    };
    superFormSeeds[fieldName] = hit.raw_value;
  }

  return { fieldPrefills, superFormSeeds };
}
